import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Inbox, Paperclip, Trash2 } from 'lucide-react'
import { cn } from './internal/utils'
import { BORDER, FOCUS_RING, RADIUS } from './internal/presets'
import { LIST_IGNORE, useUploadState } from './internal/useUploadState'
import Progress from './Progress'

const FileEntries = ({ list, listType, onRemove }) => {
  const { t } = useTranslation()
  return (
  <ul className="ui-upload-list mt-2 space-y-1">
    {list.map((entry) => (
      <li
        key={entry.uid}
        className={cn(
          'ui-upload-list-item flex items-center gap-2 border px-2 py-1.5 text-sm',
          BORDER,
          RADIUS,
          entry.status === 'error' && 'border-[var(--error-color)] text-[var(--error-color)]'
        )}
      >
        <span aria-hidden="true" className="text-[var(--text-muted)]">
          {listType === 'picture' || listType === 'picture-card' ? (
            <FileText size={14} />
          ) : (
            <Paperclip size={14} />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate">{entry.name}</span>
        {entry.status === 'uploading' ? (
          <span className="w-24">
            <Progress percent={entry.percent ?? 0} showInfo={false} size="small" />
          </span>
        ) : null}
        <button
          type="button"
          aria-label={t('common.removeNamed', { name: entry.name })}
          onClick={() => onRemove(entry)}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--error-color)]"
        >
          <Trash2 size={13} aria-hidden="true" />
        </button>
      </li>
    ))}
  </ul>
  )
}

/** Hidden native input plus the click/keyboard trigger both variants need. */
const usePicker = (addFiles, disabled) => {
  const inputRef = useRef(null)

  const open = () => {
    if (disabled) return
    inputRef.current?.click()
  }

  const renderInput = ({ multiple, accept }) => (
    <input
      ref={inputRef}
      type="file"
      multiple={multiple}
      accept={accept}
      disabled={disabled}
      className="hidden"
      onChange={(event) => {
        addFiles(event.target.files)
        event.target.value = ''
      }}
    />
  )

  return { open, renderInput }
}

/**
 * antd Upload. Supports `beforeUpload` (including `false` for manual upload,
 * which is how this app uses it), `fileList` / `defaultFileList`, `onChange`,
 * `onRemove`, `multiple`, `accept`, `maxCount`, `disabled`, `showUploadList`,
 * `listType` and `customRequest`.
 */
const Upload = ({
  fileList,
  defaultFileList = [],
  onChange,
  onRemove,
  beforeUpload,
  customRequest,
  multiple = false,
  accept,
  maxCount,
  disabled = false,
  showUploadList = true,
  listType = 'text',
  className,
  children,
  ...rest
}) => {
  const { t } = useTranslation()
  const { list, addFiles, removeFile } = useUploadState({
    fileList,
    defaultFileList,
    onChange,
    onRemove,
    beforeUpload,
    customRequest,
    multiple,
    maxCount,
  })
  const { open, renderInput } = usePicker(addFiles, disabled)

  return (
    <div className={cn('ui-upload-wrapper', className)} {...rest}>
      {renderInput({ multiple, accept })}

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={t('common.chooseFile')}
        aria-disabled={disabled}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          open()
        }}
        className={cn(
          'ui-upload ui-upload-select inline-flex',
          FOCUS_RING,
          disabled && 'cursor-not-allowed opacity-60'
        )}
      >
        {children}
      </div>

      {showUploadList && list.length > 0 ? (
        <FileEntries list={list} listType={listType} onRemove={removeFile} />
      ) : null}
    </div>
  )
}

/** antd Upload.Dragger: the same lifecycle behind a drop zone. */
const Dragger = ({
  fileList,
  defaultFileList = [],
  onChange,
  onRemove,
  beforeUpload,
  customRequest,
  multiple = false,
  accept,
  maxCount,
  disabled = false,
  showUploadList = true,
  listType = 'text',
  height,
  className,
  children,
  ...rest
}) => {
  const { t } = useTranslation()
  const { list, addFiles, removeFile } = useUploadState({
    fileList,
    defaultFileList,
    onChange,
    onRemove,
    beforeUpload,
    customRequest,
    multiple,
    maxCount,
  })
  const { open, renderInput } = usePicker(addFiles, disabled)
  const [isOver, setIsOver] = useState(false)

  return (
    <div className={cn('ui-upload-wrapper ui-upload-drag', className)} {...rest}>
      {renderInput({ multiple, accept })}

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={t('common.dropFile')}
        aria-disabled={disabled}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          open()
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (!disabled) setIsOver(true)
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(event) => {
          event.preventDefault()
          setIsOver(false)
          if (!disabled) addFiles(event.dataTransfer?.files)
        }}
        style={{ height }}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 border border-dashed px-4 py-6 text-center transition-colors',
          RADIUS,
          FOCUS_RING,
          isOver
            ? 'border-[var(--primary-color)] bg-[var(--gray-100)]'
            : 'border-[var(--border-color)] bg-[var(--surface-card)]',
          disabled && 'cursor-not-allowed opacity-60'
        )}
      >
        {children ?? (
          <>
            <Inbox size={26} aria-hidden="true" className="text-[var(--primary-color)]" />
            <p className="text-sm text-[var(--text-body)]">
              Cliquez ou glissez un fichier dans cette zone
            </p>
          </>
        )}
      </div>

      {showUploadList && list.length > 0 ? (
        <FileEntries list={list} listType={listType} onRemove={removeFile} />
      ) : null}
    </div>
  )
}

Upload.Dragger = Dragger
Upload.LIST_IGNORE = LIST_IGNORE

export default Upload
